import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { COACHES } from "@/data/coaches";
import usePageTitle from "@/hooks/usePageTitle";
import CoachAvatar from "@/components/CoachAvatar";

export default function CoachingLandingPage() {
  usePageTitle("AI Coaching Platform");
  return (
    <div className="bg-cream min-h-screen">
      {/* Hero */}
      <section className="bg-navy pt-32 pb-20 px-4 relative overflow-hidden">
        <div className="max-w-5xl mx-auto text-center">
          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="inline-block bg-gold/20 text-gold font-dm text-xs px-3 py-1 rounded-full mb-5 font-semibold">
            AI Coaching Platform
          </motion.span>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-playfair text-white text-4xl sm:text-5xl lg:text-6xl font-bold mb-5 leading-tight">
            Coaching. On Your Terms. <span className="text-gold">World-Class.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="font-dm text-white/70 text-lg max-w-3xl mx-auto mb-8">
            Seven expert coaches. ICF-aligned methodology. Available 24/7. Personalised to your goals.
          </motion.p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/coaching/start" data-testid="coaching-hero-cta" className="inline-block bg-gold text-navy font-dm font-bold px-10 py-3 rounded-lg hover:bg-gold/90 transition-colors">
              Start Your Free Session
            </Link>
            <Link to="/coaching/dashboard" data-testid="coaching-dashboard-link" className="inline-block border border-gold text-gold font-dm font-bold px-8 py-3 rounded-lg hover:bg-gold/10 transition-colors">
              My Sessions
            </Link>
          </div>
        </div>
      </section>

      {/* Coach Showcase */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <h2 className="font-playfair text-navy text-3xl font-bold text-center mb-3">Meet Your Coaches</h2>
        <p className="font-dm text-navy/60 text-center mb-10">Seven distinct voices. Choose the one that matches the conversation you need today.</p>
        <div className="flex sm:grid sm:grid-cols-2 lg:grid-cols-4 gap-4 overflow-x-auto pb-4 sm:pb-0 sm:overflow-visible snap-x">
          {COACHES.map((c, i) => (
            <motion.div key={c.id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="min-w-[260px] sm:min-w-0 snap-start bg-white rounded-2xl p-5 shadow-md hover:-translate-y-1 transition-transform border-t-4" style={{ borderColor: c.fg }} data-testid={`coach-card-${c.id}`}>
              <CoachAvatar coach={c} size={72} ring className="mb-3" />
              <h3 className="font-playfair text-navy text-lg font-bold leading-tight">{c.name}</h3>
              <p className="font-dm text-navy/50 text-xs mb-1">{c.flag} {c.origin} · <span className="text-gold font-bold">{c.level}</span></p>
              <p className="font-dm text-navy/70 text-xs italic mb-3">"{c.signature}"</p>
              <p className="font-dm text-navy/60 text-[11px]">{c.best_for.split(".")[0]}.</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-white py-16">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="font-playfair text-navy text-3xl font-bold text-center mb-12">How It Works</h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { n: 1, t: "Choose your coach and topic", d: "Browse 7 distinctive coaches. Pick a topic from 8 categories or describe your own." },
              { n: 2, t: "Select session length", d: "15, 30, 45, or 60 minutes. Your first session is on us." },
              { n: 3, t: "Start coaching", d: "Begin immediately. No waiting, no scheduling. Voice or text — your choice." },
            ].map((s) => (
              <div key={s.n} className="text-center" data-testid={`step-${s.n}`}>
                <div className="w-14 h-14 rounded-full bg-navy text-gold mx-auto flex items-center justify-center font-playfair text-2xl font-bold mb-3">{s.n}</div>
                <h3 className="font-playfair text-navy text-xl font-bold mb-2">{s.t}</h3>
                <p className="font-dm text-navy/60 text-sm">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing strip */}
      <section className="bg-cream py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <span className="inline-block bg-green-100 text-green-700 font-dm text-xs font-bold px-3 py-1 rounded-full mb-3">FIRST SESSION FREE</span>
          <h2 className="font-playfair text-navy text-2xl font-bold mb-3">Free first session for all registered users</h2>
          <p className="font-dm text-navy/60 text-sm mb-6">After that, sessions cost 3 / 5 / 7 / 10 assessment tokens (15 / 30 / 45 / 60 minutes).</p>
          <Link to="/pricing#coaching" className="inline-block border border-navy text-navy font-dm font-bold px-6 py-2.5 rounded-lg hover:bg-navy hover:text-gold transition-colors">View pricing</Link>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-gold py-12 text-center">
        <h2 className="font-playfair text-navy text-2xl font-bold mb-3">Ready when you are.</h2>
        <Link to="/coaching/start" data-testid="coaching-bottom-cta" className="inline-block bg-navy text-gold font-dm font-bold px-10 py-3 rounded-lg hover:bg-navy/90 transition-colors">
          Start Your Free Session
        </Link>
      </section>
    </div>
  );
}
